import {useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {PodRiskTypeOption} from '../graphql/types/PodRiskReviewType';
import {
    GET_POD_RISK_TYPE_LIST,
    GetPodRiskTypeListResponse
} from '../graphql/queries/getPodRiskTypeList';

/**
 * 舆情风险类型字典（筛选区下拉 + 审核弹窗多选共用）
 *
 * 文案单一来源为服务端三语，前端按当前 locale 取，改文案无需发前端版本。
 */

export interface PodRiskTypeOptionItem {
    value: string;
    label: string;
}

export interface PodRiskTypeOptionsResult {
    /** 服务端原始字典项（已按 displayOrder 排序） */
    riskTypes: PodRiskTypeOption[];
    /** 下拉选项 */
    riskTypeOptions: PodRiskTypeOptionItem[];
    /** 类型码 → 当前语言文案；未收录的码由调用方回落显示码值 */
    riskTypeTextByCode: Record<string, string>;
    loading: boolean;
    error?: any;
}

export const usePodRiskTypeOptions = (): PodRiskTypeOptionsResult => {
    const {i18n} = useTranslation();
    const currentLang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];

    const {data, loading, error} = useQuery<GetPodRiskTypeListResponse>(GET_POD_RISK_TYPE_LIST);

    const riskTypes = data?.getPodRiskTypeList || [];

    // 缺当前语言时回落 en，再回落首个，最后回落码值
    const riskTypeTextByCode: Record<string, string> = {};
    riskTypes.forEach(riskType => {
        if (!riskType.code) {
            return;
        }
        const matched =
            riskType.texts?.find(entry => entry.lang === currentLang) ||
            riskType.texts?.find(entry => entry.lang === 'en') ||
            riskType.texts?.[0];
        riskTypeTextByCode[riskType.code] = matched?.text || riskType.code;
    });

    const riskTypeOptions: PodRiskTypeOptionItem[] = riskTypes
        .filter(riskType => !!riskType.code)
        .map(riskType => ({
            value: riskType.code as string,
            label: riskTypeTextByCode[riskType.code as string]
        }));

    return {
        riskTypes,
        riskTypeOptions,
        riskTypeTextByCode,
        loading,
        error
    };
};
